// Event ordering — how the trader app ranks its event lists (SM-355).
//
// One GLOBAL config in market-service decides the server-side default
// sort of every public event list (home grid, explore, category pages),
// in one of two MODES:
//
//   * AUTO — an ordered ladder of (field, direction) criteria: the top
//     rung is the primary sort, each rung below breaks the ties of the
//     one above.
//   * MANUAL — the published catalog itself, in the order the operator
//     arranged it. The list is NOT a membership to curate: there is no
//     adding and no removing, because every published event is already
//     in it. The operator only decides where each one sits.
//
// Manual mode STARTS FROM THE RULES: the list arrives in the order the
// ladder currently produces, so the operator edits a real feed (nudge
// one event up, push another down) instead of assembling one. "Reset to
// rule order" throws the hand-arrangement away and re-ranks by the
// ladder. Events published after the last save join at the bottom, in
// rule order, flagged so they are easy to place.
//
// Plus a "pinned events stay first" switch that keeps the existing
// pin-to-top feature working. It ranks whatever the arrangement does
// not cover — anything past the cap, and anything published since the
// last save — so nothing set on another page can jump over an order set
// by hand here. The server always adds a final event-id tie-break, so
// the order is total and offset paging on the trader app can't skip or
// repeat rows.
//
// The mode is the page's main axis, so it changes the page: in manual
// mode the event list becomes the primary editing surface and the
// ladder demotes to a second section explaining where new events land.
// Same two ordering modes, same wording (Auto · / Manual ·) as the
// per-event outcome order in EventDetailPanel, which flips its
// child-market list between ranking by chance % and an admin-dragged
// order.
//
// Interaction model, deliberately:
//
//   * Reordering = drag (the ⋮⋮ handle, fast path) OR the Move up/down
//     buttons (keyboard path — SM-314's languages page set that
//     precedent and drag-only would regress it). Both lists work the
//     same way, so the gesture transfers.
//   * Direction per rung is a SegmentedControl whose labels speak the
//     field's language ("Newest first", "A → Z") instead of asc/desc.
//   * Nothing persists until Save (config-page posture, like the fee
//     flags — a half-dragged ladder must not go live), and the preview
//     rail renders the DRAFT against the real catalog server-side, so
//     the operator sees the exact client order before committing. The
//     preview asks for the first page only — the catalog can be large,
//     and ordering happens in the database either way.
//
// Writes gate on TOPIC_UPDATE (events are topics on the admin surface;
// no new RBAC codes — the SM-283 posture). Reads are open to anyone on
// the catalog surface; the server rejects unauthorized writes.
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Skeleton } from "@/components/ui/Skeleton";
import { PublishSwitch } from "@/components/markets/PublishSwitch";
import { namedActor, useAdminMap } from "@/hooks/useAdminNames";
import { useMyPerms } from "@/hooks/useMyPerms";
import { cn } from "@/lib/cn";
import { fmtDate } from "@/lib/format";
import { Perm } from "@/lib/perms";
import {
  criteriaCSV,
  eventOrderingCatalog,
  getEventOrdering,
  keys,
  previewEventOrdering,
  putEventOrdering,
} from "@/lib/queries";
import type {
  EventItem,
  EventOrderCriterion,
  EventOrderDirection,
  EventOrderMode,
  EventOrderingConfig,
  ManualOrderEvent,
} from "@/lib/types";

export const Route = createFileRoute("/_app/event-ordering")({
  component: EventOrderingPage,
});

// Per-field copy. Direction labels speak the field's own language —
// "desc on a date" means newest first, "asc on a name" means A → Z —
// so the operator never has to translate asc/desc in their head.
// Fields the server adds later fall back to their raw name with
// generic labels rather than breaking the page.
const FIELD_META: Record<
  string,
  { label: string; hint: string; dir: Record<EventOrderDirection, string> }
> = {
  published_at: {
    label: "Published",
    hint: "When the event first went live",
    dir: { desc: "Newest first", asc: "Oldest first" },
  },
  created_at: {
    label: "Created",
    hint: "When the event was authored",
    dir: { desc: "Newest first", asc: "Oldest first" },
  },
  updated_at: {
    label: "Last updated",
    hint: "Most recently edited content",
    dir: { desc: "Recent first", asc: "Oldest edits first" },
  },
  name: {
    label: "Name",
    hint: "Alphabetical by event title",
    dir: { asc: "A → Z", desc: "Z → A" },
  },
};

function fieldMeta(field: string) {
  return (
    FIELD_META[field] ?? {
      label: field,
      hint: "",
      dir: { asc: "Ascending", desc: "Descending" } as Record<
        EventOrderDirection,
        string
      >,
    }
  );
}

const MODE_OPTIONS: { value: EventOrderMode; label: string }[] = [
  { value: "auto", label: "Auto · rules" },
  { value: "manual", label: "Manual · drag" },
];

// The preview is one page on purpose: ordering happens in the database
// and the catalog can be large — the rail shows the top of the feed,
// not a mirror of it.
const PREVIEW_LIMIT = 8;

// One row of the arranged list as the PAGE holds it: the id is the
// setting, the rest is what makes it readable.
//
//   * `stale` — the server could not resolve the id to an event any
//     more. The feed already ignores it and the next save drops it; the
//     row stays visible so the operator can see the gap rather than
//     wonder where a position went.
//   * `unplaced` — a published event that is NOT in the stored order
//     yet, i.e. one published since the last save. It joins at the
//     bottom in rule order, flagged, and saving fixes its place.
interface ManualRow extends ManualOrderEvent {
  stale: boolean;
  unplaced: boolean;
}

interface Draft {
  mode: EventOrderMode;
  pinned_first: boolean;
  criteria: EventOrderCriterion[];
  manual: ManualRow[];
}

// Which list a drag is happening in. The page has two reorderable
// lists in manual mode and an index alone would let a drag started in
// one continue in the other.
type DragState = { list: "criteria" | "manual"; idx: number } | null;

// The arranged list: the stored order first (manual_event_ids, resolved
// through manual_events — an id present in the first and missing from
// the second is stale by definition), then every published event the
// stored order does not mention, in the order the RULES rank them.
//
// That second half is what removes "add" from this page: a newly
// published event is never missing from the list, it is simply at the
// bottom until someone drags it. `catalog` is empty while it loads, and
// in Auto mode it is not fetched at all — the stored order still
// renders, so switching modes shows something immediately.
function manualRowsFrom(
  cfg: EventOrderingConfig,
  catalog: EventItem[],
): ManualRow[] {
  const byID = new Map(cfg.manual_events.map((e) => [e.event_id, e]));
  const stored = new Set(cfg.manual_event_ids);
  const rows = cfg.manual_event_ids.map((id) => {
    const found = byID.get(id);
    return found
      ? { ...found, stale: false, unplaced: false }
      : { event_id: id, name: "", is_published: false, stale: true, unplaced: false };
  });
  for (const e of catalog) {
    if (stored.has(e.event_id)) continue;
    rows.push({
      event_id: e.event_id,
      name: e.name,
      icon_url: e.icon_url,
      is_published: true, // the catalog read is published-only
      stale: false,
      // "new" only means something against a stored order. With none —
      // the first time manual mode is opened — the whole list is simply
      // the rule order, and badging every row would say nothing.
      unplaced: stored.size > 0,
    });
  }
  return rows;
}

const draftKey = (d: Draft) =>
  JSON.stringify([
    d.mode,
    d.pinned_first,
    d.criteria.map((c) => `${c.field}:${c.direction}`),
    d.manual.map((m) => m.event_id),
  ]);

// Debounce a primitive: the preview refetches ~a third of a second
// after the LAST change, so a quick drag across three rows costs one
// request, not three.
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function EventOrderingPage() {
  const qc = useQueryClient();
  const perms = useMyPerms();
  const adminMap = useAdminMap();
  // Optimistic while perms load (same posture as the nav) — the server
  // still rejects unauthorized writes.
  const canManage =
    !perms.loaded || perms.isSuperAdmin || perms.has(Perm.TopicUpdate);

  const cfg = useQuery({
    queryKey: keys.eventOrdering,
    queryFn: getEventOrdering,
  });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [drag, setDrag] = useState<DragState>(null);
  // Has the OPERATOR changed anything? Tracked explicitly rather than
  // diffed against the server row, because the draft legitimately grows
  // rows the page appended (see the catalog sync below) and a diff would
  // read those as unsaved edits.
  const [touched, setTouched] = useState(false);
  // Latest draft for the dragEnd handler — avoids a stale closure on
  // the rapid dragOver → drop sequence (MarketsOrderList's ref idiom).
  const draftRef = useRef<Draft | null>(null);

  const manualMode = (draft?.mode ?? cfg.data?.mode ?? "auto") === "manual";
  const maxManual = cfg.data?.max_manual_events ?? 100;
  // The order as STORED. Declared here because the render-time syncs
  // below read it — a `const` further down would be in its temporal
  // dead zone by the time they run, which is a crash, not a warning.
  const storedIDs = new Set(cfg.data?.manual_event_ids ?? []);

  // The published catalog, in the order the RULES currently rank it —
  // the second half of the arranged list, and the source for "Reset to
  // rule order". Only fetched in manual mode: in Auto nothing on the
  // page shows individual events.
  const catalog = useQuery({
    queryKey: keys.eventOrderingCatalog(maxManual),
    queryFn: () => eventOrderingCatalog(maxManual),
    enabled: manualMode,
  });
  const catalogRows = catalog.data?.events ?? [];

  // Hydrate the draft from the server row — render-time sync with a
  // key guard (the MarketsOrderList pattern) so a save's echo becomes
  // the new baseline while a drag in flight is never clobbered. A
  // background refetch would also re-hydrate; with focus-refetch off
  // and a 30s staleTime that trade-off matches the other config pages.
  //
  // Keyed on the STORED CONFIG only. The catalog is a second, later
  // arrival, and folding it into this key would re-hydrate — and so
  // discard the operator's mode switch — the moment it landed.
  const serverDraft: Draft | null = cfg.data
    ? {
        mode: cfg.data.mode ?? "auto",
        pinned_first: cfg.data.pinned_first,
        criteria: cfg.data.criteria,
        manual: manualRowsFrom(cfg.data, catalogRows),
      }
    : null;
  const configKey = cfg.data
    ? JSON.stringify([
        cfg.data.mode ?? "auto",
        cfg.data.pinned_first,
        cfg.data.criteria.map((c) => `${c.field}:${c.direction}`),
        cfg.data.manual_event_ids,
      ])
    : "";
  const [syncedKey, setSyncedKey] = useState("");
  if (serverDraft && configKey !== syncedKey && drag === null) {
    setDraft(serverDraft);
    draftRef.current = serverDraft;
    setSyncedKey(configKey);
    setTouched(false);
  }

  // The catalog's own arrival: append the published events the draft
  // does not hold yet (published since the last save, or the whole
  // catalog the first time manual mode is opened). Appending is not an
  // edit — it does not set `touched` — and it is idempotent, so a
  // refetch cannot duplicate a row or disturb an arrangement in
  // progress.
  const catalogKey = catalogRows.map((e) => e.event_id).join(",");
  const [syncedCatalogKey, setSyncedCatalogKey] = useState("");
  if (draft && manualMode && catalogKey !== syncedCatalogKey && drag === null) {
    const have = new Set(draft.manual.map((m) => m.event_id));
    const extra = catalogRows
      .filter((e) => !have.has(e.event_id))
      .map((e) => ({
        event_id: e.event_id,
        name: e.name,
        icon_url: e.icon_url,
        is_published: true,
        stale: false,
        unplaced: storedIDs.size > 0,
      }));
    if (extra.length > 0) {
      const next = { ...draft, manual: [...draft.manual, ...extra] };
      setDraft(next);
      draftRef.current = next;
    }
    setSyncedCatalogKey(catalogKey);
  }

  // What the OPERATOR changed. Rows the catalog contributed are not an
  // edit, so they get their own signal (pendingNew) and their own
  // sentence rather than opening the page on an "unsaved" badge nobody
  // caused — but they still make the config worth saving.
  const pendingNew =
    manualMode && storedIDs.size > 0
      ? (draft?.manual.filter((m) => !storedIDs.has(m.event_id)).length ?? 0)
      : 0;
  const dirty = touched;
  const canSave = dirty || pendingNew > 0;

  const save = useMutation({
    mutationFn: (d: Draft) =>
      putEventOrdering({
        mode: d.mode,
        pinned_first: d.pinned_first,
        criteria: d.criteria,
        manual_event_ids: d.manual.map((m) => m.event_id),
      }),
    // The PUT echoes the stored, normalized row — seed the cache with
    // it so the baseline (and the "server default" hint) update without
    // a second round-trip. Stale ids the server pruned disappear from
    // the list here, which is the operator's confirmation that they
    // were dropped.
    onSuccess: (saved) => qc.setQueryData(keys.eventOrdering, saved),
    meta: { successMessage: "Event ordering saved." },
  });

  // Every OPERATOR edit goes through here (hydration and the catalog
  // append write the state directly), which is what keeps `touched`
  // honest.
  const setDraftAndRef = (next: Draft) => {
    draftRef.current = next;
    setDraft(next);
    setTouched(true);
  };

  const moveCriterion = (from: number, to: number) => {
    if (!draft || to < 0 || to >= draft.criteria.length) return;
    const criteria = [...draft.criteria];
    const [moved] = criteria.splice(from, 1);
    criteria.splice(to, 0, moved);
    setDraftAndRef({ ...draft, criteria });
  };

  const moveManual = (from: number, to: number) => {
    if (!draft || to < 0 || to >= draft.manual.length) return;
    const manual = [...draft.manual];
    const [moved] = manual.splice(from, 1);
    manual.splice(to, 0, moved);
    setDraftAndRef({ ...draft, manual });
  };

  // Throw the hand-arrangement away and take the ladder's order for the
  // whole list. Draft-only, like every other edit here — the operator
  // can look, then Discard.
  const resetToRuleOrder = () => {
    if (!draft) return;
    setDraftAndRef({
      ...draft,
      manual: catalogRows.map((e) => ({
        event_id: e.event_id,
        name: e.name,
        icon_url: e.icon_url,
        is_published: true,
        stale: false,
        unplaced: !storedIDs.has(e.event_id),
      })),
    });
  };

  const setDirection = (field: string, direction: EventOrderDirection) => {
    if (!draft) return;
    setDraftAndRef({
      ...draft,
      criteria: draft.criteria.map((c) =>
        c.field === field ? { ...c, direction } : c,
      ),
    });
  };

  const discard = () => {
    if (!serverDraft) return;
    draftRef.current = serverDraft;
    setDraft(serverDraft);
    setTouched(false);
  };

  // Preview the DRAFT — debounced so a drag across three rows costs one
  // request, keyed on the debounced shape so each arrangement caches its
  // own rows (fetch args come from the SAME debounced value as the key,
  // never a fresher draft), and holding the previous rows while the next
  // page loads so the rail never flashes empty mid-drag.
  //
  // AUTO MODE ONLY. In manual mode the arranged list IS the feed order,
  // so a preview beside it would render the same rows twice and pay a
  // round-trip to learn what the operator just dragged. The rules still
  // rank what the arrangement does not cover, and the list says so.
  const debouncedDraft = useDebounced(draft, 350);
  const previewCSV = debouncedDraft ? criteriaCSV(debouncedDraft.criteria) : "";
  const preview = useQuery({
    queryKey: keys.eventOrderingPreview(
      previewCSV,
      debouncedDraft?.pinned_first ?? true,
      PREVIEW_LIMIT,
    ),
    queryFn: () =>
      previewEventOrdering(
        debouncedDraft?.criteria ?? [],
        debouncedDraft?.pinned_first ?? true,
        PREVIEW_LIMIT,
      ),
    enabled: debouncedDraft != null && !manualMode,
    placeholderData: (prev) => prev,
  });

  const primary = draft?.criteria[0];
  const busy = save.isPending;
  const savedBy = namedActor(adminMap, cfg.data?.updated_by);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Event ordering</h1>
        <p className="text-sm text-muted">
          How the trader app ranks its event lists — home, explore and
          category pages all follow this order. Rank every event by rules,
          or arrange the catalog by hand.
        </p>
      </div>

      {/* In manual mode the arranged list IS the preview, so it takes
          the full width and no second rail is rendered — the same rows
          twice would be duplication, not confirmation. */}
      <div className={cn("grid gap-6", !manualMode && "lg:grid-cols-3")}>
        <Card className={cn(!manualMode && "lg:col-span-2")}>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle>
                {manualMode ? "Event order" : "Sort priority"}
              </CardTitle>
              {cfg.data?.is_default && !dirty && (
                <Badge tone="muted">server default</Badge>
              )}
              {dirty && <Badge tone="warning">unsaved</Badge>}
            </div>
            <div className="flex items-center gap-2">
              {dirty && (
                <Button size="sm" variant="ghost" onClick={discard} disabled={busy}>
                  Discard
                </Button>
              )}
              <Button
                size="sm"
                disabled={!canSave || !canManage || !draft}
                loading={busy}
                onClick={() => draft && save.mutate(draft)}
              >
                Save order
              </Button>
            </div>
          </CardHeader>
          <CardBody className="p-0">
            {cfg.isLoading && (
              <div className="space-y-2 p-5">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-14" />
                ))}
              </div>
            )}
            {cfg.isError && (
              <p className="px-5 py-10 text-center text-sm text-danger">
                Failed to load the ordering configuration.
              </p>
            )}

            {draft && !cfg.isLoading && (
              <>
                {/* The mode switch leads the card because it decides
                    what the rest of the card is FOR. */}
                <div className="flex items-start justify-between gap-4 border-b border-line-1 px-5 py-4">
                  <div>
                    <p className="text-xs font-medium text-t-1">Ordering mode</p>
                    <p className="text-2xs text-muted">
                      {manualMode
                        ? "The published catalog, in the order you arrange it. It starts from the rule order below."
                        : "The rules below rank the whole feed."}
                    </p>
                  </div>
                  <SegmentedControl
                    options={MODE_OPTIONS}
                    value={draft.mode}
                    onChange={(mode) => setDraftAndRef({ ...draft, mode })}
                    disabled={!canManage || busy}
                  />
                </div>

                {manualMode && (
                  <ManualList
                    rows={draft.manual}
                    canManage={canManage}
                    busy={busy}
                    loading={catalog.isLoading}
                    error={catalog.isError ? (catalog.error as Error).message : null}
                    pendingNew={pendingNew}
                    total={catalog.data?.page.total ?? draft.manual.length}
                    max={maxManual}
                    drag={drag}
                    setDrag={setDrag}
                    onMove={moveManual}
                    onReset={resetToRuleOrder}
                  />
                )}

                <div className="flex items-center justify-between gap-4 border-b border-line-1 px-5 py-4">
                  <div>
                    <p className="text-xs font-medium text-t-1">
                      {manualMode
                        ? "Pinned events lead the rest"
                        : "Pinned events stay first"}
                    </p>
                    <p className="text-2xs text-muted">
                      {manualMode
                        ? "Events pinned from their detail panel head whatever your arrangement does not cover — never the order you set above."
                        : "Events pinned from their detail panel keep the top of the feed no matter what the ladder says."}
                    </p>
                  </div>
                  <PublishSwitch
                    published={draft.pinned_first}
                    label={draft.pinned_first ? "On" : "Off"}
                    disabled={!canManage || busy}
                    onChange={(next) =>
                      setDraftAndRef({ ...draft, pinned_first: next })
                    }
                  />
                </div>

                {manualMode && (
                  <div className="border-b border-line-1 bg-bg-2/40 px-5 py-2.5">
                    <p className="text-2xs font-semibold uppercase tracking-wide text-t-3">
                      Where new events land
                    </p>
                    <p className="text-2xs text-muted">
                      The list above started from these rules, and they still
                      rank every event published since your last save — those
                      join at the bottom until you place them.
                    </p>
                  </div>
                )}

                <ul>
                  {draft.criteria.map((c, i) => {
                    const meta = fieldMeta(c.field);
                    // FIELD_META declares each field's directions in
                    // display order (dates lead with "Newest first",
                    // name with "A → Z") — entries preserve it.
                    const dirOptions = (
                      Object.entries(meta.dir) as [EventOrderDirection, string][]
                    ).map(([value, label]) => ({ value, label }));
                    return (
                      <li
                        key={c.field}
                        onDragOver={(e) => {
                          if (drag?.list !== "criteria" || !canManage) return;
                          e.preventDefault();
                          if (drag.idx !== i) {
                            moveCriterion(drag.idx, i);
                            setDrag({ list: "criteria", idx: i });
                          }
                        }}
                        className={cn(
                          "flex items-center gap-3 border-b border-line-1/60 px-5 py-3.5 transition-colors last:border-0",
                          drag?.list === "criteria" &&
                            drag.idx === i &&
                            "bg-bg-2 opacity-60",
                        )}
                      >
                        <span
                          draggable={canManage && !busy}
                          onDragStart={(e) => {
                            setDrag({ list: "criteria", idx: i });
                            e.dataTransfer.effectAllowed = "move";
                          }}
                          onDragEnd={() => setDrag(null)}
                          aria-hidden
                          className={cn(
                            "select-none leading-none tracking-tighter text-t-4",
                            canManage && !busy
                              ? "cursor-grab active:cursor-grabbing"
                              : "opacity-40",
                          )}
                        >
                          ⋮⋮
                        </span>
                        <span
                          className={cn(
                            "num inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border text-2xs font-semibold",
                            i === 0 && !manualMode
                              ? "border-neon/40 bg-neon/10 text-t-1"
                              : "border-line-1 bg-bg-2 text-t-3",
                          )}
                          aria-hidden
                        >
                          {i + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="flex items-center gap-2 text-xs font-medium text-t-1">
                            {meta.label}
                            {i === 0 && !manualMode && (
                              <Badge tone="accent">primary</Badge>
                            )}
                          </p>
                          {meta.hint && (
                            <p className="truncate text-2xs text-muted">
                              {meta.hint}
                            </p>
                          )}
                        </div>
                        <SegmentedControl
                          options={dirOptions}
                          value={c.direction}
                          onChange={(v) => setDirection(c.field, v)}
                          disabled={!canManage || busy}
                        />
                        <div className="flex items-center gap-1">
                          <MoveButton
                            dir="up"
                            name={meta.label}
                            disabled={i === 0 || !canManage || busy}
                            onClick={() => moveCriterion(i, i - 1)}
                          />
                          <MoveButton
                            dir="down"
                            name={meta.label}
                            disabled={
                              i === draft.criteria.length - 1 ||
                              !canManage ||
                              busy
                            }
                            onClick={() => moveCriterion(i, i + 1)}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <div className="space-y-1 border-t border-line-1 px-5 py-3">
                  <p className="text-2xs text-muted">
                    Drag the rows (or use the arrows) to set priority. Ties
                    fall through each rung in order; identical rows always
                    fall back to the newest event id, so paging on the trader
                    app stays stable.
                  </p>
                  {!canManage && (
                    <p className="text-2xs text-muted">
                      You need the TOPIC_UPDATE permission to change the
                      ordering.
                    </p>
                  )}
                  {/* Naming the last editor needs the admin directory,
                      which needs ADMIN_VIEW — a permission a CREATOR or
                      FINANCE reviewer does not hold. Rather than print
                      the raw ULID at the one reader who cannot resolve
                      it, drop the clause: the timestamp is the part
                      that is always true. */}
                  {cfg.data && !cfg.data.is_default && cfg.data.updated_at && (
                    <p className="text-2xs text-t-4">
                      {savedBy ? (
                        <>Last saved by {savedBy} · </>
                      ) : (
                        <>Last saved · </>
                      )}
                      {fmtDate(cfg.data.updated_at)}
                    </p>
                  )}
                  {save.isError && (
                    <p className="text-2xs text-no">
                      Couldn&rsquo;t save the ordering:{" "}
                      {(save.error as Error).message}
                    </p>
                  )}
                </div>
              </>
            )}
          </CardBody>
        </Card>

        {!manualMode && (
        <Card className="self-start">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle>Client preview</CardTitle>
              {dirty && <Badge tone="accent">draft</Badge>}
            </div>
            {/* One template literal, not "top " + {n}: a sentence split
                across JSX text nodes reaches the translation catalogue
                as fragments, and a bare "top" / "of" is untranslatable
                on its own (it shipped that way in the Thai build). */}
            <span className="text-2xs uppercase tracking-wide text-muted">
              {`top ${PREVIEW_LIMIT}`}
            </span>
          </CardHeader>
          <CardBody className="p-0">
            {preview.isLoading && (
              <div className="space-y-2 p-5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-9" />
                ))}
              </div>
            )}
            {preview.isError && (
              <p className="px-5 py-10 text-center text-sm text-danger">
                Preview unavailable: {(preview.error as Error).message}
              </p>
            )}
            {/* Says what the rail actually knows — the preview came back
                empty — and not why. The old copy ("No published events
                yet") stated a fact about the catalog this page cannot
                establish, and on SIT it said so while 28 events were
                live. */}
            {preview.data && preview.data.events.length === 0 && (
              <p className="px-5 py-10 text-center text-sm text-muted">
                Nothing to preview. The trader feed lists only events that
                are published inside a published category.
              </p>
            )}
            {preview.data && preview.data.events.length > 0 && (
              <>
                <ul
                  className={cn(
                    "transition-opacity",
                    preview.isFetching && "opacity-60",
                  )}
                >
                  {preview.data.events.map((e, i) => (
                    <PreviewRow
                      key={e.event_id}
                      event={e}
                      rank={i + 1}
                      primary={primary}
                    />
                  ))}
                </ul>
                <p className="border-t border-line-1 px-5 py-3 text-2xs text-muted">
                  {`First ${preview.data.events.length} of ${preview.data.page.total} published events, ordered by the database — the trader app pages through the full list in exactly this order.`}
                </p>
              </>
            )}
          </CardBody>
        </Card>
        )}
      </div>
    </div>
  );
}

// The arranged list: every published event, in feed order. Same reorder
// affordances as the ladder above it and as the languages registry
// (drag handle + Move up/down), plus the one thing a hand-arranged list
// needs and a fixed ladder doesn't: a way back to the rule order.
//
// There is deliberately no add and no remove. Membership is not a
// decision here — the catalog decides it. Publishing an event puts it in
// this list (at the bottom, ranked by the rules, flagged "new");
// unpublishing takes it out of the trader's feed while its position
// waits. An operator only ever answers "in what order?".
function ManualList({
  rows,
  canManage,
  busy,
  loading,
  error,
  pendingNew,
  total,
  max,
  drag,
  setDrag,
  onMove,
  onReset,
}: {
  rows: ManualRow[];
  canManage: boolean;
  busy: boolean;
  loading: boolean;
  error: string | null;
  pendingNew: number;
  total: number;
  max: number;
  drag: DragState;
  setDrag: (d: DragState) => void;
  onMove: (from: number, to: number) => void;
  onReset: () => void;
}) {
  // The list holds the whole published catalog up to the cap. Past it,
  // the ladder still ranks the overflow — say so rather than let the
  // count quietly disagree with the catalog.
  const capped = total > max;
  return (
    <div className="border-b border-line-1">
      {loading && rows.length === 0 && (
        <div className="space-y-2 p-5">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-8" />
          ))}
        </div>
      )}
      {error && (
        <p className="px-5 pt-4 text-2xs text-no">
          Couldn&rsquo;t load the catalog: {error}
        </p>
      )}
      {rows.length === 0 && !loading ? (
        <p className="px-5 py-6 text-center text-2xs text-muted">
          No published events yet — the order fills in as the catalog
          goes live.
        </p>
      ) : (
        <ul>
          {rows.map((row, i) => (
            <li
              key={row.event_id}
              onDragOver={(e) => {
                if (drag?.list !== "manual" || !canManage) return;
                e.preventDefault();
                if (drag.idx !== i) {
                  onMove(drag.idx, i);
                  setDrag({ list: "manual", idx: i });
                }
              }}
              className={cn(
                "flex items-center gap-3 border-b border-line-1/60 px-5 py-2.5 transition-colors last:border-0",
                drag?.list === "manual" && drag.idx === i && "bg-bg-2 opacity-60",
              )}
            >
              <span
                draggable={canManage && !busy}
                onDragStart={(e) => {
                  setDrag({ list: "manual", idx: i });
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragEnd={() => setDrag(null)}
                aria-hidden
                className={cn(
                  "select-none leading-none tracking-tighter text-t-4",
                  canManage && !busy
                    ? "cursor-grab active:cursor-grabbing"
                    : "opacity-40",
                )}
              >
                ⋮⋮
              </span>
              <span
                className={cn(
                  "num inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border text-2xs font-semibold",
                  i === 0
                    ? "border-neon/40 bg-neon/10 text-t-1"
                    : "border-line-1 bg-bg-2 text-t-3",
                )}
                aria-hidden
              >
                {i + 1}
              </span>
              {row.icon_url ? (
                <img
                  src={row.icon_url}
                  alt=""
                  className="h-6 w-6 shrink-0 rounded-md object-contain"
                />
              ) : (
                <span
                  className="inline-block h-6 w-6 shrink-0 rounded-md bg-bg-3"
                  aria-hidden
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-t-1">
                  {row.stale ? row.event_id : row.name}
                </p>
                {row.stale && (
                  <p className="truncate text-2xs text-muted">
                    This event no longer exists — the feed already ignores it,
                    and saving drops it from the list.
                  </p>
                )}
                {!row.stale && !row.is_published && (
                  <p className="truncate text-2xs text-muted">
                    Not published, so traders don&rsquo;t see it — its place is
                    kept for when it goes live again.
                  </p>
                )}
              </div>
              {row.stale && <Badge tone="warning">stale</Badge>}
              {!row.stale && !row.is_published && (
                <Badge tone="muted">unpublished</Badge>
              )}
              {row.unplaced && !row.stale && row.is_published && (
                <Badge tone="accent">new</Badge>
              )}
              <div className="flex items-center gap-1">
                <MoveButton
                  dir="up"
                  name={row.name || row.event_id}
                  disabled={i === 0 || !canManage || busy}
                  onClick={() => onMove(i, i - 1)}
                />
                <MoveButton
                  dir="down"
                  name={row.name || row.event_id}
                  disabled={i === rows.length - 1 || !canManage || busy}
                  onClick={() => onMove(i, i + 1)}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
        <div className="space-y-0.5">
          {pendingNew > 0 && (
            <p className="text-2xs text-t-2">
              {pendingNew === 1
                ? "1 event published since the last save is at the bottom — save to fix its place."
                : `${pendingNew} events published since the last save are at the bottom — save to fix their places.`}
            </p>
          )}
          {capped && (
            <p className="text-2xs text-muted">
              {`Showing the first ${max} of ${total} published events; the rest follow the rules below.`}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Back to where manual mode started: the ladder's order for
              the whole list. It does not switch modes, and like every
              edit here it is a draft until Save. */}
          <Button
            size="sm"
            variant="ghost"
            disabled={!canManage || busy || loading}
            onClick={onReset}
          >
            Reset to rule order
          </Button>
          <span className="text-2xs text-t-4">
            {`${rows.length} events`}
          </span>
        </div>
      </div>
    </div>
  );
}


// One preview row: rank, icon, name, and the primary rung's value so
// the operator can SEE why the row sits where it sits. When the primary
// is Name (the value is already the title), the published date stands
// in as the secondary detail. Auto mode only — manual mode arranges the
// real list instead of previewing beside it.
function PreviewRow({
  event,
  rank,
  primary,
}: {
  event: EventItem;
  rank: number;
  primary: EventOrderCriterion | undefined;
}) {
  const field =
    !primary || primary.field === "name" ? "published_at" : primary.field;
  const raw =
    field === "created_at"
      ? event.created_at
      : field === "updated_at"
        ? event.updated_at
        : event.published_at;
  const pinned = (event.display_order ?? 0) < 0;
  return (
    <li className="flex items-center gap-3 border-b border-line-1/60 px-5 py-2.5 last:border-0">
      <span
        className={cn(
          "num w-5 shrink-0 text-right font-mono text-2xs",
          rank === 1 ? "text-t-1" : "text-t-4",
        )}
        aria-hidden
      >
        {rank}
      </span>
      {event.icon_url ? (
        <img
          src={event.icon_url}
          alt=""
          className="h-6 w-6 shrink-0 rounded-md object-contain"
        />
      ) : (
        <span
          className="inline-block h-6 w-6 shrink-0 rounded-md bg-bg-3"
          aria-hidden
        />
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-t-1">{event.name}</p>
        {/* The field label stays a plain JSX child so the copy
            catalogue can translate it — inside a template literal it
            would render as one computed string and fall through to
            English (which is what a first pass here did). "First N of
            M …" above is the opposite case: there the fragments were
            bare words like "of", untranslatable on their own. */}
        <p className="truncate text-2xs text-muted">
          {fieldMeta(field).label} · {fmtDate(raw)}
        </p>
      </div>
      {pinned && <Badge tone="accent">pinned</Badge>}
    </li>
  );
}

// Move up / Move down — a real <button> (keyboard-reachable) with a
// spelled-out accessible name; the glyph is decorative. Same control as
// the languages registry (SM-314) so reordering feels identical across
// the two pages.
function MoveButton({
  dir,
  name,
  disabled,
  onClick,
}: {
  dir: "up" | "down";
  name: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={`Move ${name} ${dir}`}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "inline-flex h-7 w-7 items-center justify-center rounded-md border border-line-1 text-xs text-t-2 transition-colors",
        "focus:outline-none focus-visible:ring-4 focus-visible:ring-neon/15",
        disabled
          ? "cursor-not-allowed opacity-40"
          : "hover:border-line-3 hover:bg-bg-3",
      )}
    >
      <span aria-hidden>{dir === "up" ? "↑" : "↓"}</span>
    </button>
  );
}
